import React, { useEffect, useState } from 'react';
import { TranslationUtils, ValidationUtils } from '../../utils';
import { useDebounce, useTripStoreUpdater } from '../../hooks';
import { useSelector } from 'react-redux';
import { Button, Loading, ManageGuestCount } from '../../components';
import * as _ from 'lodash';
import { useHistory } from 'react-router-dom';
import tripService from '../../services/trip/tripService';
import ddLogger from '../../logging/ddLogger';
import { AppRoutes } from '../../routes';
import './ModifyGuests.scss';
import { OccupancyCost, OccupancyCounts, OccupancyKeys } from '../../types/occupancy';
import { ManageGuestConfirmationProps } from '../manage-guest-confirmation/ManageGuestConfirmation';
import { AlertMessage, Icon } from '@vacasa/react-components-lib';
import { StoreState } from '../../types/store.type';
import { TripState } from '../../types/trip.store.type';
import segmentService, { Step } from '../../services/segment/segmentService';
import { TripAdjustmentResponse } from 'guest-data-types-ts-lib/types';
import { AddonPetAdjustmentPreviewRequest } from 'guest-data-types-ts-lib/types/adjustments';
import { AdjustmentConfig, Splits } from '../../types/split.type';
import { isFeatureAvailableForChannel, useFeatureConfig } from '../../services/optimizely/optimizelyHelper';

export const ModifyPets: React.FC = () => {
    useTripStoreUpdater(false, true, true);

    const trip = useSelector((store: { trip: TripState }) => store.trip);
    const [initialOccupancy, setInitialOccupancy] = useState<OccupancyCounts | null>(null);
    const [occupancyCounts, setOccupancyCounts] = useState<OccupancyCounts | null>(null);
    const [renderedCounts, setRenderedCounts] = useState<OccupancyCounts | null>(null);
    const [occupancyCost, setOccupancyCost] = useState<OccupancyCost | undefined>(undefined);
    const [isFetchingPreview, setIsFetchingPreview] = useState<boolean>(false);
    const [adjustmentPreview, setAdjustmentPreview] = useState<TripAdjustmentResponse | null>(null);
    const [isWaitingForDebounceCallback, setIsWaitingForDebounceCallback] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const [isRefundFlow, setIsRefundFlow] = useState<boolean>(false);
    const [doRefund, setDoRefund] = useState<boolean>(false);
    const {
        isEnabled: isManagePetsAvailable,
        config: managePetsConfig,
        clientReady: managePetClientReady,
    } = useFeatureConfig<AdjustmentConfig>(Splits.GUEST_PORTAL_NEW_PET_ADJUSTMENT, false);

    const debounceDelay = 1000;
    const { language } = useSelector((store: StoreState) => store.user);
    const history = useHistory();
    const { home_info, reservation, guests, unit, stay } = trip;
    const isNewAddPetAvailable = isFeatureAvailableForChannel(reservation!, managePetClientReady, managePetsConfig, isManagePetsAvailable);

    let phoneNumber = ValidationUtils.validateAndGetEnvVar('REACT_APP_TOLL_FREE_PHONE_NUMBER');

    const petsSlug = TranslationUtils.get('pets');

    useEffect(() => {
        if (home_info && guests) {
            const { pets } = guests.current_counts;

            if (_.isNil(stay) || !stay.pets_adjustment_available || !isManagePetsAvailable || !isNewAddPetAvailable) {
                history.push(AppRoutes.RESERVATION);
            }

            const initialOccupancyLocal = {
                [OccupancyKeys.PETS]: { name: petsSlug, count: pets, max: guests.max_counts.pets },
            } as OccupancyCounts;

            if (_.isEqual(initialOccupancyLocal, initialOccupancy)) {
                return;
            }

            setInitialOccupancy(initialOccupancyLocal);
            setRenderedCounts(initialOccupancyLocal);
        }
    }, [home_info, guests]);

    useEffect(() => {
        setRenderedCounts((current) => {
            if (current) {
                current[OccupancyKeys.PETS] = { ...current[OccupancyKeys.PETS], name: petsSlug };

                return current;
            }
            return null;
        });
    }, [language]);

    const getAdjustmentPreview = async (
        reservationId: string,
        request: AddonPetAdjustmentPreviewRequest
    ): Promise<TripAdjustmentResponse | undefined> => {
        try {
            setError(null);
            setIsFetchingPreview(true);
            const { attributes: preview } = (await tripService.getAdjustmentPreview(reservationId, request)) as {
                attributes: TripAdjustmentResponse;
            };
            segmentService.trackAdjustmentSuccess(Step.PREVIEW, preview, {});
            return preview;
        } catch (e) {
            const code = e.code;
            ddLogger.error(`ERROR previewing updatePetAdjustment adjustment for ${reservationId}`, {
                adjustment: 'updatePetAdjustment',
                error: e.message,
                code,
            });
            segmentService.trackAdjustmentIssue(request, code);
            setError(e);
        } finally {
            setIsFetchingPreview(false);
        }
    };

    useDebounce(
        async () => {
            if (!occupancyCounts || !reservation || isFetchingPreview) {
                return;
            }

            const currPets = occupancyCounts[OccupancyKeys.PETS].count;

            if (initialOccupancy?.pets.count === currPets) {
                setOccupancyCost(undefined);
                return;
            }

            const request: AddonPetAdjustmentPreviewRequest = {
                name: 'updatePetAdjustment',
                data: {
                    pets: currPets,
                },
            };

            const preview = await getAdjustmentPreview(reservation.id, request);

            if (!preview) {
                return;
            }

            setAdjustmentPreview(preview);

            const { pets, detail, delta } = preview;

            const isRefundFlow = !_.isNil(detail?.refund);

            const doRefund = isRefundFlow && detail!.refund!.amount > 0;

            let totalAmount = isRefundFlow ? detail!.total : delta!.total;

            const newCounts = {
                [OccupancyKeys.PETS]: { ...occupancyCounts[OccupancyKeys.PETS], count: pets },
            } as OccupancyCounts;

            const newCost: OccupancyCost = {
                total: { total: totalAmount, currency: delta!.currency },
                adjustment: { total: delta!.total, currency: delta!.currency },
                previousBalance: isRefundFlow
                    ? {
                          total: balance,
                          currency: delta!.currency,
                      }
                    : undefined,
            };

            setRenderedCounts(newCounts);
            setOccupancyCost(newCost);

            setIsRefundFlow(isRefundFlow);
            setIsWaitingForDebounceCallback(false);
            setDoRefund(doRefund);
        },
        debounceDelay,
        [occupancyCounts]
    );

    if (trip.isLoading) {
        return <Loading />;
    }

    if (!home_info || !reservation || !guests) {
        return null;
    }

    if (unit) {
        phoneNumber = unit.local_phone_number;
    }

    const { max_occupancy } = home_info;
    const { balance } = reservation;

    const hasRefundInProgress = balance < 0;

    const handleGuestCountChange = async (updatedCounts: OccupancyCounts) => {
        setRenderedCounts(updatedCounts);
        setOccupancyCounts(updatedCounts);
        setIsWaitingForDebounceCallback(true);
    };

    const handleGuestCountReset = () => {
        setRenderedCounts(initialOccupancy);
        setOccupancyCounts(initialOccupancy);
        setOccupancyCost(undefined);
    };

    const handleConfirm = () => {
        const adjustmentConfirmationProps: ManageGuestConfirmationProps = {
            initialOccupancy,
            occupancyCounts,
            preview: adjustmentPreview,
        };

        if (isRefundFlow) {
            history.push({
                pathname: AppRoutes.MODIFY_CONFIRMATION,
                state: adjustmentConfirmationProps,
            });
        } else {
            history.push({
                pathname: AppRoutes.MODIFY_TRIP_ADJUSTMENTS.replace(':adjustment', 'modify_pets'),
                state: adjustmentConfirmationProps,
            });
        }
    };

    const getBtnTitle = () => {
        if (isRefundFlow && doRefund) {
            return TranslationUtils.get('add_pet_refund_btn');
        }
        if (isRefundFlow) {
            return TranslationUtils.get('add_pet_update_balance_title');
        }

        return TranslationUtils.get('next');
    };

    const maxCounts = { max: max_occupancy, rules: [OccupancyKeys.PETS] };

    const isDifferentThanOriginal = occupancyCounts && !_.isEqual(initialOccupancy, occupancyCounts);
    const isConfirmEnabled = isDifferentThanOriginal && !isWaitingForDebounceCallback;
    const btnTitle = getBtnTitle();
    const maxPets = guests.max_counts.pets;
    return (
        <div id={'main-add-pets'}>
            <h1 id={'title-add-pets'} tabIndex={0} className="heading-large page-title">
                {TranslationUtils.get('manage_pets_title')}
            </h1>
            {hasRefundInProgress && <AlertMessage type="warning" text={TranslationUtils.get('trip_eligibility_refund_in_progress')} />}
            {!hasRefundInProgress && renderedCounts && (
                <>
                    <div id={'instructions_add_pet'} className={'body-small'}>
                        <p>
                            {maxPets > 1
                                ? TranslationUtils.get('instructions_for_modify_pets', { pets: maxPets })
                                : TranslationUtils.get('instructions_for_modify_pet')}
                        </p>
                        <p> {TranslationUtils.get('extra_instructions_for_modify_pet')} </p>
                    </div>

                    {renderedCounts && (
                        <ManageGuestCount
                            counts={renderedCounts}
                            maxCount={maxCounts}
                            onChange={handleGuestCountChange}
                            onReset={handleGuestCountReset}
                            isLoading={isFetchingPreview}
                            isResetDisabled={isFetchingPreview || isWaitingForDebounceCallback}
                            isError={!!error}
                            cost={occupancyCost}
                            isDogMode={true}
                        />
                    )}
                    {error && (
                        <div id={'add-pet-error'} className="occupancy-adjustment-error">
                            <Icon.AlertTriangle />
                            <p className={'body-small'}>
                                <span>{TranslationUtils.get('manage_pets_error')}</span>.
                            </p>
                        </div>
                    )}
                    <Button
                        data-testid="btn-modify-pets-confirm"
                        className="body-regular btn-modify-guests-confirm mt-3"
                        variant="primary"
                        type="submit"
                        id="btn-modify-pets-confirm"
                        label={btnTitle}
                        disabled={!isConfirmEnabled}
                        isLoading={isFetchingPreview}
                        onClick={handleConfirm}
                    />
                </>
            )}
        </div>
    );
};
